import React, { useEffect, useMemo, useState } from 'react';
import { Zap } from 'lucide-react';
import { DEFAULT_ENERGY_CALC, buildEnergyVars, evaluateEnergyRow, parseDMY, todayDMY } from '../utils/energyCalc';

// Public "Karmik Enerji" block. `config` = energy_calc from the page config,
// `birthDate` = {day, month, year} already entered on the page (prefill).
const EnergyCalculator = ({ config, birthDate, testIdPrefix = 'energy' }) => {
  const cfg = config && Array.isArray(config.tabs) && config.tabs.length ? config : DEFAULT_ENERGY_CALC;
  const [tabId, setTabId] = useState(cfg.tabs[0]?.id);
  const [bday, setBday] = useState('');
  const [bmonth, setBmonth] = useState('');
  const [byear, setByear] = useState('');
  const [target, setTarget] = useState(todayDMY());
  const [computed, setComputed] = useState(null);

  useEffect(() => {
    if (!birthDate?.day) return;
    setBday(String(birthDate.day).padStart(2, '0'));
    setBmonth(String(birthDate.month).padStart(2, '0'));
    setByear(String(birthDate.year));
  }, [birthDate]);

  const activeTab = useMemo(() => cfg.tabs.find((t) => t.id === tabId) || cfg.tabs[0], [cfg, tabId]);

  const handleCalc = () => {
    const t = parseDMY(target);
    const d = parseInt(bday, 10), m = parseInt(bmonth, 10), y = parseInt(byear, 10);
    if (!t || !d || !m || !y || d > 31 || m > 12) {
      setComputed({ error: 'Doğum tarixi və hədəf tarixi düzgün daxil edin (GG.AA.İİİİ).' });
      return;
    }
    setComputed({ vars: buildEnergyVars({ day: d, month: m, year: y }, t) });
  };

  const rows = computed?.vars ? (activeTab?.rows || []).map((r) => ({ ...r, res: evaluateEnergyRow(r, computed.vars) })) : [];

  return (
    <div className="energy-card" data-testid={`${testIdPrefix}-card`}>
      <h3 className="energy-title"><Zap size={18} /> {cfg.title || 'Karmik Enerji Hesaplama'}</h3>

      <div className="energy-tabs" role="tablist">
        {cfg.tabs.map((t) => (
          <button key={t.id} type="button" role="tab"
            className={`energy-tab ${activeTab?.id === t.id ? 'energy-tab-active' : ''}`}
            onClick={() => setTabId(t.id)}
            data-testid={`${testIdPrefix}-tab-${t.id}`}>
            {t.name}
          </button>
        ))}
      </div>

      <div className="energy-fields">
        <label className="energy-field">
          <span>Doğum günü</span>
          <input className="karma-input energy-input" inputMode="numeric" maxLength={2} value={bday}
            onChange={(e) => setBday(e.target.value.replace(/\D/g, ''))} placeholder="GG"
            data-testid={`${testIdPrefix}-bday`} />
        </label>
        <label className="energy-field">
          <span>Doğum ayı</span>
          <input className="karma-input energy-input" inputMode="numeric" maxLength={2} value={bmonth}
            onChange={(e) => setBmonth(e.target.value.replace(/\D/g, ''))} placeholder="AA"
            data-testid={`${testIdPrefix}-bmonth`} />
        </label>
        <label className="energy-field">
          <span>Doğum ili</span>
          <input className="karma-input energy-input" inputMode="numeric" maxLength={4} value={byear}
            onChange={(e) => setByear(e.target.value.replace(/\D/g, ''))} placeholder="İİİİ"
            data-testid={`${testIdPrefix}-byear`} />
        </label>
        <label className="energy-field energy-field-wide">
          <span>Hədəf tarix</span>
          <input className="karma-input energy-input" value={target}
            onChange={(e) => setTarget(e.target.value)} placeholder="GG.AA.İİİİ"
            data-testid={`${testIdPrefix}-target`} />
        </label>
      </div>

      <button type="button" className="karma-button energy-btn" onClick={handleCalc} data-testid={`${testIdPrefix}-calc-btn`}>
        Hesabla
      </button>

      {computed?.error && <div className="energy-error" data-testid={`${testIdPrefix}-error`}>{computed.error}</div>}

      {rows.length > 0 && (
        <div className="energy-results" data-testid={`${testIdPrefix}-results`}>
          {rows.map((r) => (
            <div key={r.id} className="energy-row" data-testid={`${testIdPrefix}-row-${r.id}`}>
              <span className="energy-row-label">{r.label}</span>
              <span className="energy-row-value">{r.res ? r.res.display : '—'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EnergyCalculator;
